<template>
  <gg-card>
    <gg-card-text class="pa-3">
      <div class="d-flex flex-wrap align-center mb-3">
        <div class="body-1 mr-auto">
          <v-icon size="24" color="primary" class="pb-1" aria-hidden="true">mdi-monitor</v-icon>
          <span class="ml-2">Console</span>
          <span class="kvm-caption text-caption ml-2">Graphical — for troubleshooting. For everyday access use the Terminal.</span>
        </div>
        <v-chip
          small
          label
          role="status"
          aria-live="polite"
          class="kvm-status mr-2"
          :class="'kvm-status--' + status">
          <span class="kvm-sr-only">Console status: </span>{{ statusChip.text }}
        </v-chip>
        <gg-btn small tile outlined color="primary" class="kvm-action kvm-action--green mr-2"
                :disabled="status !== 'connected'"
                aria-label="Paste text into the console"
                @click="openPasteDialog">
          <v-icon left small aria-hidden="true">mdi-clipboard-text</v-icon>Paste
        </gg-btn>
        <gg-btn small tile outlined color="primary" class="kvm-action kvm-action--green"
                :disabled="status !== 'connected'"
                aria-label="Send Ctrl+Alt+Del to the console"
                @click="sendCtrlAltDel">
          Ctrl+Alt+Del
        </gg-btn>
      </div>

      <!-- Paste popup: prefilled from the clipboard (best-effort), editable, then
           typed into the console as keystrokes. -->
      <v-dialog v-model="pasteDialog" max-width="520">
        <gg-card role="dialog" aria-modal="true" aria-labelledby="kvm-paste-title">
          <gg-card-text class="pa-4">
            <div id="kvm-paste-title" class="body-1 mb-1">Paste to console</div>
            <p class="kvm-caption text-caption mb-3">
              Review or edit the text, then send it — it is typed into the console as
              keystrokes. A trailing line break will submit the line.
            </p>
            <v-textarea
              v-model="pasteText"
              outlined dense autofocus
              :rows="6"
              :counter="maxPaste"
              aria-label="Text to send to the console"
              placeholder="Paste (Ctrl+V) or type text here…"
              hide-details="auto"
              @keydown.stop
            />
            <div class="text-right mt-3">
              <gg-btn text class="kvm-action kvm-action--green mr-2" @click="pasteDialog = false">Cancel</gg-btn>
              <gg-btn tile color="primary" class="kvm-action kvm-action--fill" :disabled="!pasteText" @click="sendPaste">
                <v-icon left small aria-hidden="true">mdi-send</v-icon>Send to console
              </gg-btn>
            </div>
          </gg-card-text>
        </gg-card>
      </v-dialog>

      <v-alert v-if="error" type="error" border="left" dense class="mb-3">
        {{ error }}
      </v-alert>

      <v-alert v-if="server.power_state && server.power_state !== 'online'"
               type="info" border="left" dense class="mb-3">
        This server is {{ server.power_state }}. The console will connect automatically
        once it is powered on.
      </v-alert>

      <p id="kvm-console-instructions" class="kvm-caption text-caption mb-2">
        Your keyboard and mouse control this server while this page is open. Use the
        Paste or Ctrl+Alt+Del buttons above; to stop sending keystrokes, focus a text
        field or switch to another page.
      </p>

      <!-- noVNC renders its canvas into this element. Height is sized to fill the
           viewport (see resizeScreen) so the image is as large as possible. -->
      <div
        ref="screen"
        class="vds-console-screen"
        role="group"
        aria-label="Server console (graphical display)"
        aria-describedby="kvm-console-instructions"
        :style="{ height: screenHeight + 'px' }">
        <div v-if="status !== 'connected'" class="vds-console-placeholder" aria-hidden="true">
          <v-progress-circular v-if="status === 'connecting'" indeterminate color="primary" class="mb-2" />
          <div>{{ placeholderText }}</div>
        </div>
      </div>
    </gg-card-text>
  </gg-card>
</template>

<script>
import { mapActions } from 'vuex'
import RFB from '@novnc/novnc/core/rfb'
import kvm from '@/api/kvm'

// Cap a single paste so a huge clipboard can't flood the VNC server with keys.
const MAX_PASTE_CHARS = 10000

// Auto-reconnect: after a dropped/failed session, retry on a fixed 5s cadence
// (only while the console is on-screen and the server is powered on).
const RECONNECT_DELAY_MS = 5000

// While the server is powered off Proxmox won't open a VNC session, so we don't
// mint any; KvmServiceView re-reads the power state on this cadence instead, and
// we connect once it reports online.
const POWER_WAIT_INTERVAL_MS = 10000

// Space to leave below the screen (card padding) so growing the console to fill the
// viewport never introduces a page scrollbar. Tune if a gap/scrollbar appears.
const SCREEN_BOTTOM_RESERVE = 50
const SCREEN_MIN_HEIGHT = 320

export default {
  name: 'HostingKvmConsole',
  props: {
    // Provided by KvmServiceView via <router-view :server :loading>.
    server: { type: Object, default: () => ({}) },
    loading: { type: Boolean, default: false },
    // KvmServiceView.watchPower — quietly re-reads the server until it reaches a
    // power state; returns a cancel function.
    onPowerChange: { type: Function, default: null },
  },
  data: () => ({
    rfb: null,
    // idle | waiting (server powered off) | connecting | connected | disconnected | error
    status: 'idle',
    stopPowerWait: null, // cancels KvmServiceView's power-state watch
    error: '',
    // On-screen (keep-alive activated) vs cached in the background. We only
    // actively retry while on-screen; a drop while hidden reconnects on return.
    active: false,
    // Suppresses auto-reconnect (set on an unrecoverable auth failure / teardown).
    intentional: false,
    // True while we're retrying a dropped session (drives the "Reconnecting…" label).
    reconnecting: false,
    // The entitlement id the live session belongs to (to detect VM switches).
    connectedId: null,
    reconnectTimer: null,
    // True while the page-wide keyboard capture is installed.
    kbCaptured: false,
    // Dynamic screen height (px) — fills the viewport down to SCREEN_BOTTOM_RESERVE.
    screenHeight: 500,
    // Paste popup.
    pasteDialog: false,
    pasteText: '',
  }),
  computed: {
    maxPaste () { return MAX_PASTE_CHARS },
    // Unknown (not yet loaded) counts as on, so we still try rather than wait forever.
    poweredOff () {
      return !!this.server.power_state && this.server.power_state !== 'online'
    },
    statusChip () {
      switch (this.status) {
        case 'waiting': return { text: 'Waiting for power on', color: 'grey' }
        case 'connected': return { text: 'Connected', color: 'primary' }
        case 'connecting': return { text: this.reconnecting ? 'Reconnecting…' : 'Connecting…', color: 'warning' }
        case 'error': return { text: 'Error', color: 'red' }
        case 'disconnected': return { text: 'Disconnected', color: 'grey' }
        default: return { text: 'Not connected', color: 'grey' }
      }
    },
    placeholderText () {
      if (this.status === 'waiting') return 'The server is powered off. The console will connect when it starts.'
      if (this.status === 'connecting') return this.reconnecting ? 'Reconnecting to the console…' : 'Opening console session…'
      if (this.status === 'disconnected') return 'Disconnected — click Connect to reconnect.'
      if (this.status === 'error') return this.error || 'Console unavailable.'
      return 'Connecting to the console…'
    },
  },
  methods: {
    ...mapActions(['reportError']),
    // Open the paste popup, prefilled (best-effort) from the clipboard. The user can
    // edit it or paste their own, then send. Typing it as key events (rather than
    // noVNC's clipboardPasteFrom, which only sets the server clipboard) works on any
    // guest — a shell, a getty, GRUB — with no in-guest clipboard agent required.
    async openPasteDialog () {
      if (!this.rfb || this.status !== 'connected') return
      this.pasteText = ''
      this.pasteDialog = true
      // Best-effort prefill; if the browser blocks it, the user pastes manually.
      if (navigator.clipboard && navigator.clipboard.readText) {
        try {
          const text = await navigator.clipboard.readText()
          if (this.pasteDialog) this.pasteText = text || ''
        } catch (e) { /* permission denied — manual paste still works */ }
      }
    },
    sendPaste () {
      const text = this.pasteText || ''
      if (!text) return
      if (text.length > MAX_PASTE_CHARS) {
        this.reportError(new Error(`Text is too large to paste (limit ${MAX_PASTE_CHARS} characters).`))
        return
      }
      this.pasteDialog = false
      this.typeText(text)
      this.pasteText = ''
    },
    // Send each character to the VM as a keypress. Printable chars use their Unicode
    // keysym (== code point for Latin-1; 0x01000000+cp above that); newlines and
    // tabs map to Return/Tab. Shifted characters get an explicit Shift around them,
    // or the server maps the keysym to its base key without applying shift — e.g.
    // '&' (keysym 0x26) lands on the '7' key and types '7'.
    typeText (text) {
      const XK_SHIFT_L = 0xffe1
      const normalized = text.replace(/\r\n?/g, '\n')
      for (const ch of normalized) {
        let keysym
        if (ch === '\n') keysym = 0xff0d // XK_Return
        else if (ch === '\t') keysym = 0xff09 // XK_Tab
        else {
          const cp = ch.codePointAt(0)
          keysym = cp < 0x100 ? cp : 0x01000000 + cp
        }
        const shift = this.needsShift(ch)
        try {
          if (shift) this.rfb.sendKey(XK_SHIFT_L, 'ShiftLeft', true)
          this.rfb.sendKey(keysym, null) // down + up
          if (shift) this.rfb.sendKey(XK_SHIFT_L, 'ShiftLeft', false)
        } catch (e) {
          break
        }
      }
    },
    // Whether a character requires Shift on a US keyboard, so we send it explicitly.
    // Covers uppercase letters and the shifted number-row / punctuation symbols.
    needsShift (ch) {
      if (ch >= 'A' && ch <= 'Z') return true
      return '~!@#$%^&*()_+{}|:"<>?'.indexOf(ch) !== -1
    },
    async connect () {
      if (!this.server.id) {
        this.error = 'No server selected.'
        this.status = 'error'
        return
      }
      // Always tear down any previous session — console tickets are single-use.
      this.clearReconnectTimer()
      this.cleanup()
      this.intentional = false
      this.error = ''
      this.status = 'connecting'
      try {
        // Mint a fresh session immediately before connecting (60s TTL).
        const { websocket, password } = await kvm.getConsole(this.server.id)
        if (!websocket) throw new Error('The console session did not include a connection URL.')

        const rfb = new RFB(this.$refs.screen, websocket, {
          credentials: { password },
        })
        rfb.scaleViewport = true
        rfb.background = '#000'
        rfb.addEventListener('connect', this.onConnect)
        rfb.addEventListener('disconnect', this.onDisconnect)
        rfb.addEventListener('securityfailure', this.onSecurityFailure)
        this.rfb = rfb
        this.connectedId = this.server.id
      } catch (e) {
        this.status = 'error'
        this.error = e.message || 'Could not open the console session.'
        this.maybeReconnect()
      }
    },
    onConnect () {
      this.status = 'connected'
      this.error = ''
      this.reconnecting = false
      if (this.active) this.captureKeyboard()
    },
    onDisconnect (e) {
      const wasConnected = this.status === 'connected'
      this.rfb = null
      if (this.intentional) {
        this.status = 'disconnected'
        return
      }
      // Any unexpected drop (clean server-side close or a hard failure) → recover.
      this.status = wasConnected ? 'error' : 'connecting'
      this.error = wasConnected ? 'Connection lost — reconnecting…' : ''
      this.maybeReconnect()
    },
    onSecurityFailure (e) {
      // Auth failures won't fix themselves on retry — stop and surface it.
      this.intentional = true
      this.clearReconnectTimer()
      this.status = 'error'
      const reason = e && e.detail && e.detail.reason
      this.error = reason ? `Console authentication failed: ${reason}` : 'Console authentication failed.'
    },
    // Retry on a fixed 5s cadence. Only while on-screen — a drop while the console
    // is in the background reconnects when the user returns (activated).
    maybeReconnect () {
      this.clearReconnectTimer()
      if (this.intentional) return
      if (!this.active) {
        this.status = 'disconnected'
        return
      }
      if (this.poweredOff) {
        this.waitForPower()
        return
      }
      this.reconnecting = true
      this.status = 'connecting'
      // A failure may mean the server just went down; have the parent re-read the
      // power state once before the retry, so ensureConnected() can switch to
      // waiting instead of minting another doomed session.
      if (this.onPowerChange) this.stopPowerWait = this.onPowerChange()
      this.reconnectTimer = setTimeout(() => {
        this.reconnectTimer = null
        if (this.poweredOff) this.waitForPower()
        else this.connect()
      }, RECONNECT_DELAY_MS)
    },
    // Server is powered off: drop any session, stop retrying, and let the parent
    // poll the power state; the server.power_state watcher connects on 'online'.
    waitForPower () {
      this.clearReconnectTimer()
      this.cleanup()
      this.status = 'waiting'
      this.error = ''
      this.reconnecting = false
      if (this.onPowerChange) {
        this.stopPowerWait = this.onPowerChange('online', { interval: POWER_WAIT_INTERVAL_MS, tries: Infinity })
      }
    },
    cancelPowerWait () {
      if (this.stopPowerWait) {
        this.stopPowerWait()
        this.stopPowerWait = null
      }
    },
    // Connect if we aren't already; if the live session belongs to a different VM
    // (param switch), reconnect to the current one. A powered-off server waits.
    ensureConnected () {
      if (!this.server.id) return
      if (this.poweredOff) {
        if (this.status !== 'waiting') this.waitForPower()
        return
      }
      if (this.status === 'connecting') return
      if (this.status === 'connected') {
        if (this.connectedId !== this.server.id) this.connect()
        return
      }
      this.connect()
    },
    sendCtrlAltDel () {
      if (this.rfb) this.rfb.sendCtrlAltDel()
    },
    // Send every keystroke on the page to the VM, not just when the canvas is
    // focused: re-point noVNC's keyboard handler (canvas-bound by default) at the
    // document. A capture-phase guard leaves real form fields alone. Uses noVNC
    // internals (_keyboard); guarded so a version change degrades to default focus.
    captureKeyboard () {
      const kb = this.rfb && this.rfb._keyboard
      if (!kb) return
      try {
        kb.ungrab() // drop the canvas-bound listeners noVNC added on connect
        kb._target = document
        kb.grab() // re-add them on the document so focus no longer matters
      } catch (e) {
        return // noVNC internals changed — leave default (click-to-focus) behaviour
      }
      if (!this.kbCaptured) {
        document.addEventListener('keydown', this.guardEditableKeys, true)
        document.addEventListener('keyup', this.guardEditableKeys, true)
        this.kbCaptured = true
      }
    },
    releaseKeyboard () {
      const kb = this.rfb && this.rfb._keyboard
      if (kb) { try { kb.ungrab() } catch (e) { /* already gone */ } }
      if (this.kbCaptured) {
        document.removeEventListener('keydown', this.guardEditableKeys, true)
        document.removeEventListener('keyup', this.guardEditableKeys, true)
        this.kbCaptured = false
      }
    },
    // Don't steal keystrokes meant for a real input/textarea/select/editable.
    guardEditableKeys (e) {
      const t = e.target
      if (!t) return
      const tag = t.tagName
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || t.isContentEditable) {
        e.stopPropagation() // keep it from reaching noVNC's document handler
      }
    },
    clearReconnectTimer () {
      if (this.reconnectTimer) {
        clearTimeout(this.reconnectTimer)
        this.reconnectTimer = null
      }
    },
    // Grow the screen to fill the space from its top down to the viewport bottom
    // (less the reserve). noVNC's scaleViewport keeps the host aspect ratio within
    // it, so a taller box just means a bigger image — width is never the limit here.
    resizeScreen () {
      if (!this.active) return
      this.$nextTick(() => {
        const el = this.$refs.screen
        if (!el) return
        const top = el.getBoundingClientRect().top
        const avail = window.innerHeight - top - SCREEN_BOTTOM_RESERVE
        this.screenHeight = Math.max(SCREEN_MIN_HEIGHT, Math.floor(avail))
      })
    },
    // Detach listeners and close the socket without surfacing a spurious error.
    cleanup () {
      this.releaseKeyboard()
      if (this.rfb) {
        this.rfb.removeEventListener('connect', this.onConnect)
        this.rfb.removeEventListener('disconnect', this.onDisconnect)
        this.rfb.removeEventListener('securityfailure', this.onSecurityFailure)
        try { this.rfb.disconnect() } catch (e) { /* already gone */ }
        this.rfb = null
      }
    },
  },
  // Recompute the screen height when the layout above it changes (alerts toggling,
  // the status row wrapping, etc.).
  watch: {
    status () { this.resizeScreen() },
    error () { this.resizeScreen() },
    'server.power_state' (state) {
      this.resizeScreen()
      if (!this.active) return
      if (state === 'online' && this.status === 'waiting') {
        this.cancelPowerWait()
        this.connect()
      } else if (this.poweredOff && this.status !== 'connected' && this.status !== 'waiting') {
        // Went down while connecting/retrying — stop minting sessions.
        this.waitForPower()
      }
    },
  },
  // First render of the (kept-alive) component — it is on-screen, so auto-connect.
  mounted () {
    this.active = true
    window.addEventListener('resize', this.resizeScreen)
    this.resizeScreen()
    this.ensureConnected()
  },
  // Returning to the console after viewing another page in this VM. If the session
  // is still alive this is a no-op (instantly ready); otherwise it reconnects.
  activated () {
    this.active = true
    this.resizeScreen()
    this.ensureConnected()
    if (this.status === 'connected') this.captureKeyboard()
  },
  // Navigated to another page in this VM: stay connected, just stop active retries
  // and stop grabbing the keyboard (so typing on other pages isn't sent to the VM).
  deactivated () {
    this.active = false
    this.clearReconnectTimer()
    this.cancelPowerWait()
    this.releaseKeyboard()
  },
  // Only fires when the keep-alive cache is destroyed — i.e. we left the VM.
  beforeDestroy () {
    this.intentional = true
    this.clearReconnectTimer()
    this.cancelPowerWait()
    this.cleanup()
    window.removeEventListener('resize', this.resizeScreen)
  },
}
</script>

<style scoped lang="scss">
@import './kvm-ui';

// Contrast-safe secondary text for the sub-title caption, helper copy and the
// console keyboard/mouse instructions (replaces the faint global grey).
.kvm-caption {
  @include kvm-secondary-text;
}

.kvm-sr-only {
  @include kvm-sr-only;
}

// Status badge: text-backed (never colour-alone), AA-contrast in both themes.
// Fixes white-on-amber / white-on-green failures by owning fg+bg per state.
.kvm-status {
  font-weight: 600;
}
.kvm-status--connected {
  // #fff on #0a7c3c = 5.30:1; badge vs card 5.30:1 (light) / 3.41:1 (dark).
  background-color: #0a7c3c !important;
  color: #ffffff !important;
}
.kvm-status--connecting {
  // dark text on amber (white-on-amber fails); ~10.7:1. Border defines the
  // badge boundary on a light card (#b07400 vs #fff ≈ 3.9:1).
  background-color: #ffb70f !important;
  color: rgba(0, 0, 0, 0.87) !important;
  border: 1px solid #b07400;
}
.kvm-status--error {
  // #fff on #c62828 = 5.63:1.
  background-color: #c62828 !important;
  color: #ffffff !important;
}
.kvm-status--disconnected,
.kvm-status--waiting,
.kvm-status--idle {
  // #fff on #5b6670 = 5.87:1.
  background-color: #5b6670 !important;
  color: #ffffff !important;
}
.theme--dark {
  .kvm-status--disconnected,
  .kvm-status--waiting,
  .kvm-status--idle {
    // #fff on #6b7681 = 4.63:1; badge vs dark card ≈ 3.9:1.
    background-color: #6b7681 !important;
  }
}
.kvm-status ::v-deep .v-chip__content {
  color: inherit;
}

// Visible focus ring (global default is missing/too weak) + comfortable mobile
// touch target for the console action buttons.
.kvm-action {
  &:focus-visible {
    @include kvm-focus-ring;
  }
  @media (max-width: $kvm-bp-mobile) {
    @include kvm-min-target(44px);
  }
}

// Outlined/text green buttons (Paste, Ctrl+Alt+Del, Cancel): brand green text on
// white is only ~3.15:1 — bump to AA-contrast green in both themes.
.kvm-action--green {
  @include kvm-text-green-aa;
}

// Filled primary button (Send to console): white on brand green is only ~3.15:1 —
// switch to white on a deeper green (5.3:1).
.kvm-action--fill {
  @include kvm-btn-primary-aa;
}

.vds-console-screen {
  position: relative;
  width: 100%;
  min-height: 320px;
  background: #000;
  border-radius: 4px;
  overflow: hidden;
  &:focus-visible {
    @include kvm-focus-ring;
  }
}
.vds-console-placeholder {
  position: absolute;
  top: 0; left: 0; right: 0; bottom: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #cfd6dc; // ~14:1 on the black screen
  text-align: center;
  padding: 16px;
}
</style>
